import Orders from "components/templates/MyPageTemplate/Orders";
import { getDateStringFromToday } from "utils/TimeUtil";
import { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "store";
import { LogLevel, log } from "utils/LogUtil";
import { PointsTransaction, SubscriptionPayment, OrderInfo } from "common/models/types";
import { eventLog } from "utils/AmplitudeUtil";
import { fetchPointsTransactions, fetchOrders } from "api/pointsService";
import { fetchMySubscriptionPayments } from "api/subscriptionService";
import { loadMySubscription } from "store/slices/subscriptionSlice";
import { useSubscriptionReconcile } from "hooks/useSubscriptionReconcile";

/**
 * 탭2 "내역" — 좌측: 포인트 거래내역(충전·사용·환불·지급 전체), 우측: 구독 결제이력.
 * 구독 상태 카드는 탭1 "현황"으로 일원화되어 여기서는 제거됨.
 */
const OrdersPage = () => {
  const [startAt, setStartAt] = useState<string>(getDateStringFromToday({ months: -1 }));
  const [endAt, setEndAt] = useState<string>(getDateStringFromToday({}));
  const [transactions, setTransactions] = useState<PointsTransaction[]>([]);
  const [subscriptionPayments, setSubscriptionPayments] = useState<SubscriptionPayment[]>([]);
  // 충전 영수증 URL은 주문(OrderInfo.payReceiptUrl)에만 있어, 거래내역의 충전 항목에 매핑하기 위해 함께 조회.
  const [orders, setOrders] = useState<OrderInfo[]>([]);
  const [, setIsLoading] = useState(true);

  const dispatch = useDispatch<AppDispatch>();
  const subscription = useSelector((s: RootState) => s.subscription.me);
  const isReconciling = useSelector((s: RootState) => s.subscription.isReconciling);
  useSubscriptionReconcile();

  log(LogLevel.VERBOSE, "OrdersPage");

  const loadAll = useCallback(async () => {
    setIsLoading(true);
    // Promise.allSettled — 한쪽 실패가 다른 쪽을 막지 않도록 격리.
    const [txResult, subResult, orderResult] = await Promise.allSettled([
      fetchPointsTransactions({
        startDate: startAt,
        endDate: endAt,
      }),
      fetchMySubscriptionPayments({
        limit: 100,
        startDate: startAt,
        endDate: endAt + "T23:59:59",
      }),
      fetchOrders({
        startDate: startAt,
        endDate: endAt + "T23:59:59",
      }),
    ]);

    if (txResult.status === "fulfilled") {
      log(LogLevel.INFO, "포인트 거래내역 조회 결과:", txResult.value);
      setTransactions(txResult.value);
    } else {
      log(LogLevel.ERROR, "포인트 거래내역 조회 실패:", txResult.reason);
      setTransactions([]);
    }

    if (subResult.status === "fulfilled") {
      const subRes = subResult.value;
      if (subRes.error) {
        log(LogLevel.WARNING, "구독 결제이력 조회 에러 (무시):", subRes.message);
        setSubscriptionPayments([]);
      } else {
        setSubscriptionPayments(subRes.data || []);
      }
    } else {
      log(LogLevel.ERROR, "구독 결제이력 조회 실패:", subResult.reason);
      setSubscriptionPayments([]);
    }

    if (orderResult.status === "fulfilled") {
      setOrders(orderResult.value);
    } else {
      log(LogLevel.ERROR, "충전 주문(영수증) 조회 실패:", orderResult.reason);
      setOrders([]);
    }

    setIsLoading(false);
  }, [startAt, endAt]);

  useEffect(() => {
    eventLog("포인트구독내역_진입", {});
    log(LogLevel.VERBOSE, "OrdersPage: mounted");
    loadAll();
    dispatch(loadMySubscription());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startAt, endAt]);

  // 결제 완료 후 isReconciling가 false로 바뀌면 내역 새로고침
  useEffect(() => {
    if (!isReconciling && subscription?.status === "ACTIVE") {
      loadAll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReconciling, subscription?.status]);

  return (
    <Orders
      transactions={transactions}
      subscriptionPayments={subscriptionPayments}
      orders={orders}
      startAt={startAt}
      endAt={endAt}
      setStartAt={setStartAt}
      setEndAt={setEndAt}
    />
  );
};

export default OrdersPage;
